/**
 * Zustand store for Admin Settings
 * Wraps the existing localStorage persistence and storefront snapshot merge.
 */
import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { type Grade, type ScreenCondition, type DisplayCurrency, getPriceKey } from "@/data/catalog";
import { sanitizeText, sanitizePhone, sanitizeUrl, sanitizeWhatsAppNumber } from "@/lib/sanitize";

// ─── Types (re-exported from here) ────────────────────────────

export interface LockedExchangeRate {
  rate: number;
  lockedAt: number;
  source: "api" | "manual";
}

export interface SmsSettings {
  enabled: boolean;
  provider: "twilio" | "other";
  apiKeySid: string;
  apiKeySecret: string;
  fromNumber: string;
}

export interface DailySummarySettings {
  enabled: boolean;
  whatsappNumber: string;
  emailAddress: string;
  sendWhatsApp: boolean;
  sendEmail: boolean;
}

export interface BannerAd {
  id: string;
  imageUrl: string;
  linkUrl: string;
  position: "top" | "bottom";
  enabled: boolean;
  label?: string;
}

export interface InvoiceSettings {
  companyName: string;
  tagline: string;
  footerText: string;
  termsText: string;
}

export interface GradeColor {
  bg: string;
  text: string;
}

export interface AdminSettings {
  marginPercent: number;
  deviceExtraProfits: Record<string, number>;
  priceOverrides: Record<string, number>;
  deviceMargins: Record<string, number>;
  lockedRate: LockedExchangeRate;
  lockedUsdRate: LockedExchangeRate;
  displayCurrency: DisplayCurrency;
  smsSettings: SmsSettings;
  dailySummarySettings: DailySummarySettings;
  lowStockThresholds: Record<string, number>;
  whatsappNumber: string;
  bannerAds: BannerAd[];
  deviceCommissionRates: Record<string, number>;
  swapDeviceCommissionRates: Record<string, number>;
  shippingFee: number;
  customGrades: string[];
  gradeOrder: string[];
  deletedDefaultGrades: string[];
  gradeColors: Record<string, GradeColor>;
  archivedGrades: string[];
  /** Maps grade name → array of category keys it applies to (empty = all categories) */
  gradeCategories: Record<string, string[]>;
  subconditionsEnabled: boolean;
  invoiceSettings: InvoiceSettings;
  reviewsInOrderForm: boolean;
  reviewsOnStorefront: boolean;
  inactivityThresholdMinutes: number;
  staffLoginAlerts: boolean;
  staffInactivityAlerts: boolean;
  commissionAutoPercent: number;
  /** Per-category commission auto % overrides — keyed by category key */
  categoryCommissionPercents?: Record<string, number>;
  /** Per-device commission auto % overrides — keyed by device name */
  deviceCommissionPercents?: Record<string, number>;
  deviceSortMode: "oldest" | "newest" | "az";
  /** Master switch for transactional email notifications. Shipping-related statuses bypass this. */
  emailNotificationsEnabled?: boolean;
  /** Master switch for admin/staff notification emails (orders, enquiries, reviews, etc.). */
  adminNotificationEmailsEnabled?: boolean;
  /** Optional display name threaded into admin-notification email subject/body. */
  emailSenderName?: string;
  /** Per-audience channel toggles consulted by dispatchers before sending. */
  channelToggles?: {
    customerEmail: boolean;
    customerWhatsApp: boolean;
    staffEmail: boolean;
    staffWhatsApp: boolean;
    adminEmail: boolean;
    adminWhatsApp: boolean;
  };
  statusNotificationMode: "status_based" | "general" | "both";
  /** Whether payment collection is enabled on the storefront */
  paymentsEnabled: boolean;
}

// ─── Constants ─────────────────────────────────────────────────

const ADMIN_STORE_KEY = "khays_admin_settings";
const RATE_LOCK_DURATION = 24 * 60 * 60 * 1000;
export const FALLBACK_CNY_RATE = 1.56;
export const FALLBACK_USD_RATE = 14.5;
const DEFAULT_MARGIN = 0.08;

function readStorageJson<T>(key: string, fallback: T): T {
  try {
    const stored = localStorage.getItem(key);
    if (stored) return JSON.parse(stored) as T;
  } catch {}
  return fallback;
}

function normalizeGradeName(grade: string | null | undefined): string | null {
  const normalized = grade?.trim();
  if (!normalized || normalized === "Default" || normalized === "__standalone__") return null;
  return normalized;
}

function getGradeFromCompositeKey(key: string): string | null {
  const parts = key.split("|");
  return normalizeGradeName(parts[2]);
}

function collectReferencedGrades(source: unknown, grades: Set<string>): void {
  if (!source) return;

  if (Array.isArray(source)) {
    source.forEach(item => collectReferencedGrades(item, grades));
    return;
  }

  if (typeof source !== "object") return;

  Object.entries(source as Record<string, unknown>).forEach(([key, value]) => {
    const gradeFromKey = getGradeFromCompositeKey(key);
    if (gradeFromKey) grades.add(gradeFromKey);

    if (key === "grade" && typeof value === "string") {
      const normalized = normalizeGradeName(value);
      if (normalized) grades.add(normalized);
    }

    if (key === "enabledGrades" && Array.isArray(value)) {
      value.forEach(item => {
        if (typeof item !== "string") return;
        const normalized = normalizeGradeName(item);
        if (normalized) grades.add(normalized);
      });
    }

    if (value && typeof value === "object") {
      collectReferencedGrades(value, grades);
    }
  });
}

function getReferencedGradeList(settings: AdminSettings): string[] {
  // NOTE: Referenced grades are intersected with the configured grade set
  // in getOrderedGrades(), so scanning external localStorage blobs (which can
  // be very large: device settings, stock, media, links, condition guide)
  // adds no information to the final output. Limiting the scan to admin-
  // settings fields keeps grade resolution O(settings) instead of O(catalog)
  // and prevents catastrophic re-render slowdowns.
  const referencedGrades = new Set<string>();

  Object.keys(settings.gradeColors || {}).forEach(grade => {
    const normalized = normalizeGradeName(grade);
    if (normalized) referencedGrades.add(normalized);
  });

  collectReferencedGrades(settings.priceOverrides || {}, referencedGrades);
  collectReferencedGrades(settings.deviceExtraProfits || {}, referencedGrades);
  collectReferencedGrades(settings.deviceMargins || {}, referencedGrades);

  return Array.from(referencedGrades);
}

function getDefaultInvoiceSettings(): InvoiceSettings {
  return {
    companyName: "Khay's_Pre-Order Premium Devices",
    tagline: "Ghana's Trusted Source for Genuine Devices",
    footerText: "Thank you for your order! For enquiries, contact us via WhatsApp.",
    termsText: "All prices in Ghana Cedis (GH₵). Prices subject to change.",
  };
}

function getDefaultSettings(): AdminSettings {
  return {
    marginPercent: DEFAULT_MARGIN,
    deviceExtraProfits: {},
    deviceMargins: {},
    priceOverrides: {},
    lockedRate: { rate: FALLBACK_CNY_RATE, lockedAt: 0, source: "manual" },
    lockedUsdRate: { rate: FALLBACK_USD_RATE, lockedAt: 0, source: "manual" },
    displayCurrency: "GHS",
    smsSettings: { enabled: false, provider: "twilio", apiKeySid: "", apiKeySecret: "", fromNumber: "" },
    dailySummarySettings: { enabled: false, whatsappNumber: "", emailAddress: "", sendWhatsApp: true, sendEmail: false },
    lowStockThresholds: {},
    whatsappNumber: "233598429052",
    bannerAds: [],
    deviceCommissionRates: {},
    swapDeviceCommissionRates: {},
    shippingFee: 500,
    customGrades: [],
    gradeOrder: [],
    deletedDefaultGrades: [],
    gradeColors: {},
    archivedGrades: [],
    gradeCategories: {},
    subconditionsEnabled: true,
    invoiceSettings: getDefaultInvoiceSettings(),
    reviewsInOrderForm: true,
    reviewsOnStorefront: true,
    inactivityThresholdMinutes: 30,
    staffLoginAlerts: true,
    staffInactivityAlerts: true,
    commissionAutoPercent: 20,
    deviceSortMode: "oldest",
    statusNotificationMode: "status_based",
    paymentsEnabled: false,
    emailNotificationsEnabled: true,
    adminNotificationEmailsEnabled: true,
    channelToggles: {
      customerEmail: true,
      customerWhatsApp: true,
      staffEmail: true,
      staffWhatsApp: true,
      adminEmail: true,
      adminWhatsApp: false,
    },
  };
}

// ─── Load from localStorage ───────────────────────────────────

function loadFromStorage(): AdminSettings {
  const defaults = getDefaultSettings();

  try {
    const stored = localStorage.getItem(ADMIN_STORE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return {
        ...defaults,
        ...parsed,
        smsSettings: { ...defaults.smsSettings, ...(parsed.smsSettings || {}) },
        dailySummarySettings: { ...defaults.dailySummarySettings, ...(parsed.dailySummarySettings || {}) },
        lowStockThresholds: parsed.lowStockThresholds || {},
        bannerAds: parsed.bannerAds || [],
        gradeColors: parsed.gradeColors || {},
        invoiceSettings: { ...defaults.invoiceSettings, ...(parsed.invoiceSettings || {}) },
        lockedRate: parsed.lockedRate || defaults.lockedRate,
        lockedUsdRate: parsed.lockedUsdRate || defaults.lockedUsdRate,
        channelToggles: { ...(defaults.channelToggles as object), ...(parsed.channelToggles || {}) } as AdminSettings["channelToggles"],
      };
    }
  } catch {}

  return defaults;
}

function persistSettings(settings: AdminSettings): void {
  // Sanitize user-controlled string fields before persisting
  settings.whatsappNumber = sanitizeWhatsAppNumber(settings.whatsappNumber || "");
  settings.bannerAds = (settings.bannerAds || []).map(ad => ({
    ...ad,
    linkUrl: sanitizeUrl(ad.linkUrl || ""),
    imageUrl: sanitizeUrl(ad.imageUrl || ""),
    label: sanitizeText(ad.label || "", 200),
  }));
  if (settings.invoiceSettings) {
    settings.invoiceSettings.companyName = sanitizeText(settings.invoiceSettings.companyName || "", 200);
    settings.invoiceSettings.tagline = sanitizeText(settings.invoiceSettings.tagline || "", 300);
    settings.invoiceSettings.footerText = sanitizeText(settings.invoiceSettings.footerText || "", 500);
    settings.invoiceSettings.termsText = sanitizeText(settings.invoiceSettings.termsText || "", 500);
  }
  localStorage.setItem(ADMIN_STORE_KEY, JSON.stringify(settings));
}

// ─── Zustand Store ────────────────────────────────────────────

interface AdminSettingsState extends AdminSettings {
  setSettings: (settings: AdminSettings) => void;
  patch: (partial: Partial<AdminSettings>) => void;
  reload: () => void;
}

export const useAdminSettings = create<AdminSettingsState>()(
  devtools(
    (set, get) => ({
      ...loadFromStorage(),

  setSettings: (settings) => {
    persistSettings(settings);
    set({ ...settings });
  },

  patch: (partial) => {
    const { setSettings, patch, reload, ...current } = get();
    const next = { ...current, ...partial } as AdminSettings;
    persistSettings(next);
    set(partial);
  },

  reload: () => {
    set(loadFromStorage());
  },
    }),
    { name: "AdminStore" }
  )
);

// ─── Backward-compatible exports ──────────────────────────────

export function loadAdminSettings(): AdminSettings {
  return useAdminSettings.getState();
}

export function updateAdminSettings(updater: AdminSettings | ((current: AdminSettings) => AdminSettings)): void {
  const next = typeof updater === "function" ? updater(useAdminSettings.getState()) : updater;
  useAdminSettings.getState().setSettings(next);
}

export function saveAdminSettings(settings: AdminSettings): void {
  updateAdminSettings(settings);
}

// ─── Helper functions ─────────────────────────────────────────

export function getDeviceExtraProfit(deviceName: string, variantLabel: string, grade: Grade, screenCondition?: ScreenCondition): number {
  const key = getPriceKey(deviceName, variantLabel, grade, screenCondition);
  return loadAdminSettings().deviceExtraProfits[key] ?? 0;
}

export function setDeviceExtraProfit(deviceName: string, variantLabel: string, grade: Grade, amount: number, screenCondition?: ScreenCondition): void {
  const key = getPriceKey(deviceName, variantLabel, grade, screenCondition);
  updateAdminSettings(current => {
    const deviceExtraProfits = { ...current.deviceExtraProfits };
    if (amount <= 0) delete deviceExtraProfits[key];
    else deviceExtraProfits[key] = amount;
    return { ...current, deviceExtraProfits };
  });
}

export function getDeviceMargin(deviceName: string, variantLabel: string, grade: Grade, screenCondition?: ScreenCondition): number | null {
  const key = getPriceKey(deviceName, variantLabel, grade, screenCondition);
  return loadAdminSettings().deviceMargins[key] ?? null;
}

export function getAdminPriceOverride(deviceName: string, variantLabel: string, grade: Grade, screenCondition?: ScreenCondition): number | null {
  const key = getPriceKey(deviceName, variantLabel, grade, screenCondition);
  return loadAdminSettings().priceOverrides[key] ?? null;
}

export function setAdminPriceOverride(deviceName: string, variantLabel: string, grade: Grade, basePrice: number, screenCondition?: ScreenCondition): void {
  const key = getPriceKey(deviceName, variantLabel, grade, screenCondition);
  updateAdminSettings(current => ({
    ...current,
    priceOverrides: { ...current.priceOverrides, [key]: basePrice },
  }));
}

export function removeAdminPriceOverride(deviceName: string, variantLabel: string, grade: Grade, screenCondition?: ScreenCondition): void {
  const key = getPriceKey(deviceName, variantLabel, grade, screenCondition);
  updateAdminSettings(current => {
    const priceOverrides = { ...current.priceOverrides };
    delete priceOverrides[key];
    return { ...current, priceOverrides };
  });
}

export function getMarginPercent(): number { return loadAdminSettings().marginPercent; }
export function setMarginPercent(percent: number): void {
  updateAdminSettings(current => ({ ...current, marginPercent: percent }));
}

export function getLockedRate(): LockedExchangeRate { return loadAdminSettings().lockedRate; }
export function getLockedUsdRate(): LockedExchangeRate { return loadAdminSettings().lockedUsdRate; }
export function isRateExpired(): boolean { return Date.now() - getLockedRate().lockedAt > RATE_LOCK_DURATION; }

export function lockRate(rate: number, source: "api" | "manual" = "api"): void {
  updateAdminSettings(current => ({
    ...current,
    lockedRate: { rate, lockedAt: Date.now(), source },
  }));
}
export function lockUsdRate(rate: number, source: "api" | "manual" = "api"): void {
  updateAdminSettings(current => ({
    ...current,
    lockedUsdRate: { rate, lockedAt: Date.now(), source },
  }));
}

export async function fetchAndLockRate(): Promise<number> {
  try {
    const res = await fetch("https://open.er-api.com/v6/latest/CNY");
    if (!res.ok) throw new Error("API error");
    const data = await res.json();
    const ghsRate = data?.rates?.GHS;
    if (ghsRate && typeof ghsRate === "number") { lockRate(ghsRate, "api"); return ghsRate; }
    throw new Error("No GHS rate");
  } catch {
    const current = getLockedRate();
    if (current.rate > 0) return current.rate;
    lockRate(FALLBACK_CNY_RATE, "manual"); return FALLBACK_CNY_RATE;
  }
}

export async function fetchAndLockUsdRate(): Promise<number> {
  try {
    const res = await fetch("https://open.er-api.com/v6/latest/USD");
    if (!res.ok) throw new Error("API error");
    const data = await res.json();
    const ghsRate = data?.rates?.GHS;
    if (ghsRate && typeof ghsRate === "number") { lockUsdRate(ghsRate, "api"); return ghsRate; }
    throw new Error("No GHS rate");
  } catch {
    const current = getLockedUsdRate();
    if (current.rate > 0) return current.rate;
    lockUsdRate(FALLBACK_USD_RATE, "manual"); return FALLBACK_USD_RATE;
  }
}

// ─── Profit Breakdown ─────────────────────────────────────────

export interface ProfitBreakdown {
  basePrice: number;
  marginAmount: number;
  marginPercent: number;
  priceWithMargin: number;
  exchangeRate: number;
  convertedGHS: number;
  manualExtraProfit: number;
  finalPriceGHS: number;
  netProfitGHS: number;
  usesYuanPricing: boolean;
}

export function calculateProfitBreakdown(
  basePrice: number, marginPercent: number, exchangeRate: number,
  usesYuanPricing: boolean, manualExtraProfit: number = 0
): ProfitBreakdown {
  const marginAmount = basePrice * marginPercent;
  const priceWithMargin = basePrice + marginAmount;
  const convertedGHS = usesYuanPricing ? Math.round(priceWithMargin * exchangeRate) : Math.round(priceWithMargin);
  const finalPriceGHS = convertedGHS + manualExtraProfit;
  const basePriceGHS = usesYuanPricing ? Math.round(basePrice * exchangeRate) : basePrice;
  const netProfitGHS = finalPriceGHS - basePriceGHS;
  return { basePrice, marginAmount, marginPercent, priceWithMargin, exchangeRate, convertedGHS, manualExtraProfit, finalPriceGHS, netProfitGHS, usesYuanPricing };
}

// ─── Grade helpers ────────────────────────────────────────────

// Lightweight memo: re-derives only when relevant settings fields change.
let _orderedGradesCache: {
  key: unknown;
  withArchived: string[];
  withoutArchived: string[];
} | null = null;

export function getOrderedGrades(includeArchived = true): string[] {
  const settings = loadAdminSettings();
  const cacheKey = [
    settings.customGrades,
    settings.gradeOrder,
    settings.deletedDefaultGrades,
    settings.archivedGrades,
    settings.gradeColors,
    settings.priceOverrides,
    settings.deviceExtraProfits,
    settings.deviceMargins,
  ];

  if (_orderedGradesCache) {
    const prev = _orderedGradesCache.key as unknown[];
    let same = prev.length === cacheKey.length;
    if (same) {
      for (let i = 0; i < cacheKey.length; i++) {
        if (prev[i] !== cacheKey[i]) { same = false; break; }
      }
    }
    if (same) {
      return includeArchived
        ? _orderedGradesCache.withArchived
        : _orderedGradesCache.withoutArchived;
    }
  }

  const deletedDefaults = settings.deletedDefaultGrades || [];
  const archived = settings.archivedGrades || [];
  const defaultGradesList = (["Grade A", "Grade B+", "Grade B"] as string[])
    .filter(g => !deletedDefaults.includes(g));
  const configuredGrades = [...defaultGradesList, ...(settings.customGrades || [])];
  const configuredSet = new Set(configuredGrades);
  const referencedGrades = getReferencedGradeList(settings);

  const ordered: string[] = [];
  const seen = new Set<string>();
  const addGrade = (grade: string | null | undefined) => {
    const normalized = normalizeGradeName(grade);
    if (!normalized || seen.has(normalized)) return;
    if (deletedDefaults.includes(normalized) && !configuredSet.has(normalized)) return;
    seen.add(normalized);
    ordered.push(normalized);
  };

  (settings.gradeOrder || []).forEach(addGrade);
  configuredGrades.forEach(addGrade);
  referencedGrades.forEach(g => {
    if (configuredSet.has(g)) addGrade(g);
  });

  const withArchived = ordered;
  const withoutArchived = ordered.filter(g => !archived.includes(g));
  _orderedGradesCache = { key: cacheKey, withArchived, withoutArchived };

  return includeArchived ? withArchived : withoutArchived;
}

/** Get only active (non-archived) grades for new device setups */
export function getActiveGrades(): string[] {
  return getOrderedGrades(false);
}

/** Check if a grade is archived */
export function isGradeArchived(grade: string): boolean {
  return (loadAdminSettings().archivedGrades || []).includes(grade);
}

/** Get ordered grades filtered by category assignment. Grades with no categories apply to all. */
export function getGradesForCategory(category: string, includeArchived = true): string[] {
  const all = getOrderedGrades(includeArchived);
  const settings = loadAdminSettings();
  const gradeCategories: Record<string, string[]> = settings.gradeCategories || {};
  return all.filter(g => {
    const cats = gradeCategories[g];
    if (!cats || cats.length === 0) return true;
    return cats.includes(category);
  });
}

const POSITION_COLORS: GradeColor[] = [
  { bg: "#22c55e", text: "#ffffff" },
  { bg: "#3b82f6", text: "#ffffff" },
  { bg: "#f59e0b", text: "#ffffff" },
  { bg: "#f97316", text: "#ffffff" },
  { bg: "#ef4444", text: "#ffffff" },
  { bg: "#8b5cf6", text: "#ffffff" },
  { bg: "#6b7280", text: "#ffffff" },
];

export function getGradeColor(grade: string, position?: number): GradeColor {
  const settings = loadAdminSettings();
  if (settings.gradeColors[grade]) return settings.gradeColors[grade];
  const idx = position ?? getOrderedGrades().indexOf(grade);
  return POSITION_COLORS[idx % POSITION_COLORS.length] || POSITION_COLORS[0];
}
